// ISDP CUSTOM: Dräger-branded light/dark AppTheme definitions plus a MUI theme
// provider wrapper that preserves Backstage's class-name prefixes in production
// so our CSS overrides keep matching. Light-by-default is seeded at startup via
// setActiveThemeId (see Root.tsx) rather than an appThemeApi override.
import { useEffect, type ReactNode } from 'react';
import type { AppTheme } from '@backstage/core-plugin-api';
import {
  StylesProvider,
  ThemeProvider as MuiV4ThemeProvider,
} from '@material-ui/core/styles';
import {
  StyledEngineProvider,
  ThemeProvider as MuiV5ThemeProvider,
} from '@mui/material/styles';
import { draegerLightTheme, draegerDarkTheme } from './theme';

const LIGHT_THEME_ID = 'draeger-light';
const DARK_THEME_ID = 'draeger-dark';

// ISDP CUSTOM: Custom MUI class name generator that preserves ALL name prefixes
// in production (not just 'Mui*'). Without this, Backstage's class names like
// 'BackstageHeader-header' become 'jss1' in production, breaking our CSS overrides.
const generateClassName = (() => {
  let counter = 0;
  return (rule: any, styleSheet?: any) => {
    counter += 1;
    const name = styleSheet?.options?.name;
    if (name) {
      return `${name}-${rule.key}-${counter}`;
    }
    return `jss${counter}`;
  };
})();

// ISDP CUSTOM: Theme provider wrapper that replaces UnifiedThemeProvider.
// UnifiedThemeProvider internally creates its own StylesProvider with the default
// createGenerateClassName which minifies Backstage* class names to 'jss4-N' in
// production. We replicate its behavior but use our custom generateClassName.
function ThemeProviderWrapper({
  theme,
  isDark,
  children,
}: {
  theme: { getTheme(version: string): any };
  isDark: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.style.colorScheme = 'dark';
      // odx-theme-dark MUST be on <body> (a descendant of :root)
      // because ODX uses :root:has(.odx-theme-dark) which checks descendants
      document.body.classList.add('odx-dark-mode', 'odx-theme-dark');
      document.body.classList.remove('odx-light-mode');
      root.classList.remove('odx-light-mode');
    } else {
      root.style.colorScheme = 'light';
      document.body.classList.add('odx-light-mode');
      document.body.classList.remove('odx-dark-mode', 'odx-theme-dark');
      root.classList.remove('odx-dark-mode', 'odx-theme-dark');
    }
  }, [isDark]);

  const v4Theme = theme.getTheme('v4');
  const v5Theme = theme.getTheme('v5');

  let result = <>{children}</>;

  if (v4Theme) {
    result = (
      <StylesProvider generateClassName={generateClassName}>
        <MuiV4ThemeProvider theme={v4Theme}>{result}</MuiV4ThemeProvider>
      </StylesProvider>
    );
  }

  if (v5Theme) {
    result = (
      <StyledEngineProvider injectFirst>
        <MuiV5ThemeProvider theme={v5Theme}>{result}</MuiV5ThemeProvider>
      </StyledEngineProvider>
    );
  }

  return result;
}

// ISDP CUSTOM: Dräger-branded light and dark themes with CSS class sync.
export const appThemes: AppTheme[] = [
  {
    id: LIGHT_THEME_ID,
    title: 'Light',
    variant: 'light',
    Provider: ({ children }) => (
      <ThemeProviderWrapper theme={draegerLightTheme} isDark={false}>
        {children}
      </ThemeProviderWrapper>
    ),
  },
  {
    id: DARK_THEME_ID,
    title: 'Dark',
    variant: 'dark',
    Provider: ({ children }) => (
      <ThemeProviderWrapper theme={draegerDarkTheme} isDark>
        {children}
      </ThemeProviderWrapper>
    ),
  },
];
